import { useEffect, useMemo, useRef } from 'react';
import { useLoadLmtScript } from './useLoadLmtScript';

type SirWidgetType = 'match.lmtPlus';

function cleanId(value: string): string {
  return value.replace(/[^a-zA-Z0-9]/g, '-');
}

export function useSirWidget(
  type: SirWidgetType,
  eventId: number,
  options?: Record<string, unknown>,
  language = 'en',
) {
  const isScriptLoaded = useLoadLmtScript(language);
  const containerId = useMemo(() => cleanId(`${type}-${eventId}`), [type, eventId]);
  const languageRef = useRef(language);
  // Inline option objects from callers must not remount the widget on every parent render
  // (live score ticks would otherwise removeWidget + addWidget in a blink loop).
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!isScriptLoaded || !window.SIR) return;

    window.SIR('addWidget', `#${containerId}`, type, {
      matchId: eventId,
      language: languageRef.current,
      ...optionsRef.current,
    });

    return () => {
      window.SIR?.('removeWidget', `#${containerId}`);
    };
  }, [containerId, eventId, isScriptLoaded, type]);

  useEffect(() => {
    if (!window.SIR) return;
    try {
      window.SIR('changeLanguage', language);
      languageRef.current = language;
    } catch {
      window.SIR('changeLanguage', 'en');
      languageRef.current = 'en';
    }
  }, [language]);

  return containerId;
}
